import { useState, useEffect } from 'react'
import moment from 'moment'
import { createContainer } from 'unstated-next'
import * as api from '../../../infrastructure/api'
import { t } from '../../../infrastructure/i18nextHelper'
import { hasFeature } from '../../../infrastructure/feature'
import { snackbars } from '../../../infrastructure/snackbars'
import { ChannelCustomerSegment, MktSaleForecast, Site, Company, SupplyProduct, CustomerSegment, CustomerSegmentProduct, ForecastByCompanyAndMonth, SnapshotType, ForecastSnapshotParam } from '../models'
import { useSnapshotLabel } from '../useSnapshotLabel'

function useForecast() {
    let [sites, setSites] = useState<Site[]>([])
    let [companies, setCompanies] = useState<Company[]>([])
    let [products, setProducts] = useState<SupplyProduct[]>([])
    let [customerSegmentGroupedByChannel, setCustomerSegmentGroupedByChannel] = useState<{ [key: string]: ChannelCustomerSegment[] }>({})
    let [customerSegment, setCustomerSegment] = useState<{ [customerSegment: string]: { [site: string]: CustomerSegmentProduct[] } }>({})
    let [mktSaleForecast, setMktSaleForecast] = useState<MktSaleForecast>({ company: null, date: null, daysInPeriod: null, values: [], snapshotsInfos: [] })
    let [channelCustomerSegments, setChannelCustomerSegments] = useState<ChannelCustomerSegment[]>([])
    let [isSnapshotCreationDialogOpen, setIsSnapshotCreationDialogOpen] = useState<boolean>(false)
    let [isSnapshotCancellationDialogOpen, setIsSnapshotCancellationDialogOpen] = useState<boolean>(false)
    let [documentKeyDate, setDocumentKeyDate] = useState<string | null>(null)
    let [startDate, setStartDate] = useState<string | null>(null)
    let [endDate, setEndDate] = useState<string | null>(null)
    let [isSecondFortnight, setIsSecondFortnight] = useState<boolean>(false)
    let [numberOfCalendarDays, setNumberOfCalendarDays] = useState<number | null>(null)
    let [snapshotType, setSnapshotType] = useState<SnapshotType | null>(null)

    useEffect(() => { loadSites() }, [mktSaleForecast.company])
    useEffect(() => { setNumberOfCalendarDays(getPeriodDuration()) }, [startDate, endDate])
    useEffect(() => setDocumentKeyDate(getPeriod().start), [startDate])

    let { getSnapshotLabel } = useSnapshotLabel()

    let loadSites = async () => {
        if (!mktSaleForecast?.company) {
            setSites([])
        } else {
            let sites = await api.get<Site[]>('stock/movement/mktSale/forecast/site?company=' + mktSaleForecast.company)
            setSites(sites)
        }
    }

    let loadForecastDatas = async () => {
        let companysPromise = api.get<Company[]>('stock/movement/mktSale/company')
        let channelCustomerSegmentsPromise = api.get<ChannelCustomerSegment[]>('stock/movement/mktSale/channelCustomerSegment')
        let productsPromise = api.get<SupplyProduct[]>('stock/movement/mktSale/product')

        await loadSites()
        let companies = await companysPromise
        let channelCustomerSegments = await channelCustomerSegmentsPromise
        let products = await productsPromise

        setCompanies(companies)
        setChannelCustomerSegments(channelCustomerSegments)
        setProducts(products)
    }

    let getPeriodDuration = () => {
        let { start, end } = getPeriod()
        if (!start || !end) return null
        let diff = moment(end).diff(start, 'days') + 1
        return diff > 0 ? diff : null
    }

    let isValidPeriod = () => {
        let duration = getPeriodDuration()
        return duration && duration > 0
    }

    let loadMktSales = async () => {
        if (!isValidPeriod()) return

        let { start, end } = getPeriod()
        let parameter: ForecastByCompanyAndMonth = { company: mktSaleForecast.company!, date: start!, endDate: end }
        let forecast = await api.get<MktSaleForecast>('stock/movement/mktsale/forecast', parameter)
        setMktSaleForecast(forecast)
    }

    let getPeriod = () => ({
        start: isSecondFortnight ? moment(startDate).add(15, 'd').format('yyyy-MM-DD') : startDate,
        end: hasFeature('MktSalesForecastByPeriod')
            ? endDate
            : hasFeature('MktSalesForecastByFortnight') && !isSecondFortnight
                ? moment(startDate).add(14, 'd').format('yyyy-MM-DD')
                : moment(startDate).add(1, 'M').add(-1, 'd').format('yyyy-MM-DD')
    })

    let isCompanyMonthFilled = () => !!mktSaleForecast && !!mktSaleForecast.company && !!startDate
    let isProductsByCustomerSegmentSet = () => Object.keys(customerSegment).length > 0

    let getSnapshot = (type: SnapshotType) => mktSaleForecast.snapshotsInfos?.find(x => x.type === type)
    let hasSavedSnapshot = (type: SnapshotType) => !!getSnapshot(type)

    let createSnapshot = async (type: SnapshotType) => {
        if (isCompanyMonthFilled()) {
            let { start, end } = getPeriod()
            if (!hasFeature('MktSalesForecastShipTo'))
                await callSave()

            await api.post<ForecastSnapshotParam>('stock/movement/mktsale/snapshot', { company: mktSaleForecast.company!, month: start!, type })
            snackbars.success(t('mktSales.forecast.saveForecastSuccess', { type: getSnapshotLabel(type) }))
            setIsSnapshotCreationDialogOpen(false)
            await loadMktSales()
        }
    }

    let cancelSnapshot = async (type: SnapshotType) => {
        if (isCompanyMonthFilled()) {
            let snapshot = getSnapshot(type)
            if (snapshot) {
                await api.del(`stock/movement/mktsale/snapshot/${snapshot.id}`)
                snackbars.success(t('mktSales.forecast.cancelForecastSuccess', { type: getSnapshotLabel(type) }))
                setIsSnapshotCancellationDialogOpen(false)
                await loadMktSales()
            }
        }
    }

    let callSave = async () => {
        mktSaleForecast.date = getPeriod().start
        await api.post('stock/movement/mktsale/forecast', mktSaleForecast)
    }

    let save = async () => {
        if (!isValidPeriod()) return
        if (!isCompanyMonthFilled()) return
        await callSave()
        await loadMktSales()
        snackbars.success(t('httpSuccess.forecastSaved'))
    }

    let setDefaultValues = () => {
        let defaultVal: MktSaleForecast = { company: null, date: null, daysInPeriod: null, values: [], snapshotsInfos: [] }
        setMktSaleForecast(defaultVal)
    }

    let loadProductByCustomerSegment = async () => {
        if (!isValidPeriod()) return
        if (!isCompanyMonthFilled()) return

        let { start, end } = getPeriod()

        let customerSegmentProducts = await api.get<CustomerSegment[]>(
            `stock/movement/mktsale/customerSegmentProduct/${mktSaleForecast.company}/${start}/${end}`)

        let productsByCustomerSegment = customerSegmentProducts
            .flatMap(x => x.products.map(product => ({ customerSegment: x.code, site: x.site, product: product })))
            .groupBy(x => x.customerSegment, x => x)
            .indexValueBy(([customerSegment, _]) => customerSegment, ([_, x]) => x.indexMultipleValueBy(x => x.site, x => x.product))

        setCustomerSegment(productsByCustomerSegment)
    }

    let toggleSecondFortnight = () => setIsSecondFortnight(!isSecondFortnight)

    let setDates = (startDate: string | null, endDate: string | null) => {
        setStartDate(startDate)
        setEndDate(endDate)
    }

    let showCreationDialog = (type: SnapshotType) => {
        setSnapshotType(type)
        setIsSnapshotCreationDialogOpen(true)
    }

    let showCancellationDialog = (type: SnapshotType) => {
        setSnapshotType(type)
        setIsSnapshotCancellationDialogOpen(true)
    }

    let hideCreationDialog = () => {
        setSnapshotType(null)
        setIsSnapshotCreationDialogOpen(false)
    }

    let hideCancellationDialog = () => {
        setSnapshotType(null)
        setIsSnapshotCancellationDialogOpen(false)
    }

    return {
        sites, products, customerSegmentGroupedByChannel, companies, channelCustomerSegments, isSnapshotCreationDialogOpen,
        mktSaleForecast, isSnapshotCancellationDialogOpen, productsByCustomerSegment: customerSegment, setDefaultValues,
        createSnapshot, cancelSnapshot, loadForecastDatas, setCustomerSegmentGroupedByChannel, loadMktSales,
        save, isCompanyMonthFilled, getSnapshot, hasSavedSnapshot, setMktSaleForecast,
        loadProductByCustomerSegment, isProductsByCustomerSegmentSet,
        isSecondFortnight, toggleSecondFortnight, startDate, setStartDate, endDate, setDates, numberOfCalendarDays, documentKeyDate,
        snapshotType, showCreationDialog, showCancellationDialog, hideCreationDialog, hideCancellationDialog
    }
}

export let ForecastContainer = createContainer(useForecast)